import { Navigate, Outlet, useLocation, useOutletContext, useParams } from "react-router-dom";
import { useAccess } from "../utils/usePermissions";
import { canOpen, firstOpenScreen } from "./screens";

/**
 * LF360-2024 — the route side of common/permissions/screens.js.
 *
 * Nothing is decided before the permissions of the current project have
 * arrived: deciding on an empty list would send everybody away from the page
 * they asked for.
 */

/**
 * Opens the screen at the current path only for a user who may open it;
 * anybody else goes to `fallback`, a section whose index sends them on to a
 * screen they may open.
 */
export const RequireScreen = ({ fallback }) => {
  const access = useAccess();
  const location = useLocation();
  const { projectCode } = useParams();
  const parentCtx = useOutletContext() || {};

  if (!access.ready) return null;

  const path = location.pathname.slice(`/${projectCode}`.length);
  if (!canOpen(access, path)) {
    return (
      <Navigate
        to={`/${projectCode}${fallback}`}
        replace
        state={{ from: location }}
      />
    );
  }

  return <Outlet context={parentCtx} />;
};

/**
 * A section's index: the first of its screens the user may open, `preferred`
 * first, or the dashboard when there is none.
 */
export const SectionIndexRedirect = ({ section, preferred }) => {
  const access = useAccess();
  const { projectCode } = useParams();

  if (!access.ready) return null;

  const screen = firstOpenScreen(access, section, preferred);
  return <Navigate to={`/${projectCode}${screen || "/dashboard"}`} replace />;
};

/** Organization users for organization admins, project users for the rest. */
export const AdministrationIndexRedirect = () => {
  const { isOrgAdmin } = useAccess();
  const preferred = isOrgAdmin ? "/administration/system-users" : "/administration/project-users";
  return <SectionIndexRedirect section="/administration" preferred={preferred} />;
};

export const ContentIndexRedirect = () => <SectionIndexRedirect section="/content" />;

/**
 * The audit trail is the analytics landing page, as before; whoever may not
 * read it lands on the first analytics screen they may open.
 */
export const AnalyticsIndex = ({ children }) => {
  const access = useAccess();

  if (!access.ready) return null;
  if (canOpen(access, "/analytics/audit-trail/subjects")) return children;
  return <SectionIndexRedirect section="/analytics" />;
};
